import { faStar as faStarSolid } from '@fortawesome/free-solid-svg-icons';
import {
  faHand,
  faMedal,
  faMessageCode,
  faStar,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation } from 'react-router-dom';
import { ROLES, SCOPES } from '../../../config/permissions/roles';
import { ADMIN_CONFIG } from '../../../config/renderConfig';
import { isAwardType } from '../../../helpers/awardTypes';
import { combine } from '../../../helpers/styles';
import { useJuror } from '../../../hooks/useAuth';
import { useConfig } from '../../../hooks/useConfig';
import { AWARD_TYPES } from '../../../models/award';
import { JUROR_GROUP_PHASES } from '../../../models/jurorGroup';
import { SESSION_TYPES } from '../../../models/session';
import Submission, { EVALUATION_STATUS } from '../../../models/submission';
import PermissionHandler from '../../../modules/permissionHandler';
import { getLogo } from '../../../helpers/logo';
import CommentModal from './commentModal';
import styles from './styles.module.scss';

const ActionsColumn = ({
  submission,
  updateSubmission,
  isAdminView,
  isReadOnly,
  isDetailPage,
}) => {
  const { jurorId: impersonatingJurorId } = useParams();
  const { role, jurorGroup, juror, session, isPreview } = useJuror();
  const [displayCommentModal, setDisplayCommentModal] = useState(false);
  const { ENTRIES_TABLE, PUBLICATION_RESTRICTION_DEADLINE } = useConfig();
  const [showPublishRestriction, setShowPublishRestriction] = useState(false);
  const { t } = useTranslation();
  const { state } = useLocation();

  const numComments = useMemo(
    () =>
      submission?.evaluations?.reduce(
        (count, evaluation) => count + (evaluation.comment ? 1 : 0),
        0
      ),
    [submission]
  );
  const nominated = submission?.nominated;
  const suggestedForNomination = useMemo(
    () =>
      submission?.suggestedForNomination ||
      (!isAwardType(AWARD_TYPES.IF_DA, session) &&
        !isAwardType(AWARD_TYPES.IF_DTA, session) &&
        submission?.hasJuryStatement) ||
      submission?.suggestedForNominationExternal ||
      (role === ROLES.chairPerson &&
        submission?.evaluations?.some(
          (evaluation) => evaluation.suggestedForNomination
        )),
    [submission, session]
  );
  const suggestedForNominationPreselection = useMemo(
    () =>
      submission?.scoresFromPriorSessions?.some(
        (scores) => scores.numSuggestedForNominations
      ),
    [submission]
  );
  const evaluationStatus = useMemo(() => {
    const currentJurorEvaluation = submission?.evaluations?.find(
      (evaluation) =>
        evaluation.jurorId === submission?.jurorId ||
        evaluation.jurorId === juror?.jurorId
    );
    return currentJurorEvaluation?.status || submission?.evaluation;
  }, [submission]);

  const showGoldDecidedAction =
    session?.sessionType === SESSION_TYPES.FINAL_JURY &&
    (suggestedForNomination || nominated);

  const hasPublishRestriction =
    new Date(
      submission?.publishRestriction || submission?.fields?.publishRestriction
    ) > PUBLICATION_RESTRICTION_DEADLINE;

  const {
    renderNominate,
    renderDelegateChairperson,
    renderComment,
    renderPrizeMoney,
  } = useMemo(() => {
    const ACTIONS = isAdminView
      ? ADMIN_CONFIG.ENTRIES_TABLE.ACTIONS
      : ENTRIES_TABLE.ACTIONS;
    return {
      renderNominate: ACTIONS.includes('nominate'),
      renderDelegateChairperson: ACTIONS.includes('delegateChairperson'),
      renderComment: ACTIONS.includes('comment'),
      renderPrizeMoney:
        ACTIONS.includes('prize-money') ||
        (isAwardType(AWARD_TYPES.IF_SIP, session) &&
          session?.prizeMoneyModeEnabled),
    };
  }, [ENTRIES_TABLE, session]);

  const handleNominate = async () => {
    const jurorGroupId =
      !isDetailPage && role === ROLES.chairPerson
        ? juror.jurorGroups.find(
            (jg) => jg.jurorGroupId === submission?.evaluations[0].jurorGroupId
          )?.jurorGroupId
        : jurorGroup.jurorGroupId;

    return await Submission.setNominated(
      !submission.nominated,
      state?.jurorGroupId || jurorGroupId || jurorGroup.jurorGroupId,
      submission.submissionId
    ).then(() => {
      updateSubmission({
        ...submission,
        nominated: !submission.nominated,
      });
    });
  };

  const suggestForNomination = async () => {
    return await Submission.setSuggestForNomination(
      !submission.suggestedForNomination,
      state?.jurorGroupId || jurorGroup.jurorGroupId,
      submission.submissionId,
      submission?.jurorId || impersonatingJurorId
    )
      .then((res) => {
        updateSubmission({
          ...submission,
          suggestedForNomination: !submission.suggestedForNomination,
        });
      })
      .catch((e) => {
        setShowPublishRestriction(true);
      });
  };

  const handleSuggestNominationClick = async () => {
    if (isAdminView && submission.hasJuryStatement) {
      return setDisplayCommentModal(true);
    }

    if (isAwardType(AWARD_TYPES.IF_SIP, session)) {
      if (submission.hasJuryStatement || submission.suggestedForNomination) {
        await Submission.deleteJuryStatement({
          jurorGroupId: jurorGroup.jurorGroupId,
          submissionId: submission.submissionId,
        });
        await Submission.setSuggestForNomination(
          false,
          jurorGroup.jurorGroupId,
          submission.submissionId
        );
        return updateSubmission({
          ...submission,
          hasJuryStatement: false,
          suggestedForNomination: false,
        });
      } else {
        return setDisplayCommentModal(true);
      }
    }

    if (isAwardType(AWARD_TYPES.IF_DTA, session)) {
      return setDisplayCommentModal(true);
    }

    return suggestForNomination();
  };

  const handleChairPersonMustDecide = async () => {
    return await Submission.setChairPersonMustDecide(
      !submission.chairPersonMustDecide,
      jurorGroup.jurorGroupId,
      submission.submissionId
    ).then(() => {
      updateSubmission({
        ...submission,
        chairPersonMustDecide: !submission.chairPersonMustDecide,
      });
    });
  };

  const handlePrizeMoneyNomination = async () => {
    const jurorGroupId = submission.evaluations[0].jurorGroupId;
    await Submission.setNominated(
      !submission.nominated,
      jurorGroupId,
      submission.submissionId
    ).then(() => {
      updateSubmission({
        ...submission,
        nominated: !submission.nominated,
      });
    });
  };

  const addComment = async ({ comment, isNewComment }) => {
    const jurorGroupId =
      !isDetailPage && role === ROLES.chairPerson
        ? juror.jurorGroups.find(
            (jg) => jg.jurorGroupId === submission?.evaluations[0].jurorGroupId
          )?.jurorGroupId
        : jurorGroup.jurorGroupId;

    await Submission.setComment(
      comment,
      state?.jurorGroupId || jurorGroupId || jurorGroup.jurorGroupId,
      submission.submissionId,
      submission?.jurorId
    ).then(() => {
      if (!isDetailPage) {
        updateSubmission({
          ...submission,
          evaluations: !submission.evaluations.some(
            (evaluation) => evaluation.jurorId === juror.jurorId
          )
            ? [
                ...submission.evaluations,
                {
                  jurorId: juror.jurorId,
                  firstname: juror.firstname,
                  lastname: juror.lastname,
                  role,
                  comment,
                },
              ]
            : submission.evaluations.map((evaluation) =>
                evaluation.jurorId === juror.jurorId
                  ? { ...evaluation, comment }
                  : evaluation
              ),
        });
      } else {
        updateSubmission({ ...submission, comment });
      }
    });
  };

  const addJuryStatement = async ({ comment }) => {
    await Submission.setJuryStatement({
      juryStatement: comment,
      jurorGroupId: jurorGroup.jurorGroupId,
      submissionId: submission.submissionId,
    });
    await Submission.setSuggestForNomination(
      true,
      jurorGroup.jurorGroupId,
      submission.submissionId
    );
    return updateSubmission({
      ...submission,
      hasJuryStatement: true,
      suggestedForNomination: true,
    });
  };

  const handleCommentSubmit = (submitData) => {
    if (
      isAwardType(AWARD_TYPES.IF_DA, session) ||
      (isAwardType(AWARD_TYPES.OTHER, session) &&
        process.env?.REACT_APP_MODE === 'taipei')
    ) {
      addComment(submitData);
    } else if (
      isAwardType(AWARD_TYPES.IF_SIP, session) ||
      isAwardType(AWARD_TYPES.IF_DTA, session)
    ) {
      addJuryStatement(submitData);
    }
    setDisplayCommentModal(false);
  };

  const handleCommentDelete = async () => {
    if (isAwardType(AWARD_TYPES.IF_DTA, session)) {
      setDisplayCommentModal(false);
      await Submission.deleteJuryStatement({
        jurorGroupId: jurorGroup.jurorGroupId,
        submissionId: submission.submissionId,
      });
      await Submission.setSuggestForNomination(
        false,
        jurorGroup.jurorGroupId,
        submission.submissionId
      );
      return updateSubmission({
        ...submission,
        hasJuryStatement: false,
        suggestedForNomination: false,
      });
    }
  };

  const onModalClose = () => {
    setDisplayCommentModal(false);
  };

  const onCommentModalShow = () => {
    setDisplayCommentModal(true);
  };

  const goldDecidedActionDisabled =
    (role !== ROLES.chairPerson && submission.chairPersonMustDecide) ||
    hasPublishRestriction ||
    (session?.sessionType === SESSION_TYPES.FINAL_JURY &&
      role !== ROLES.chairPerson);

  return (
    <>
      <CommentModal
        submission={submission}
        onClose={onModalClose}
        onSubmit={handleCommentSubmit}
        visible={displayCommentModal}
        isAdminView={isAdminView}
        onDelete={handleCommentDelete}
      />
      <div
        className={styles.buttonContainer}
        onClick={(e) => e.stopPropagation()}
      >
        {hasPublishRestriction && showPublishRestriction && (
          <span className={styles.publishRestriction}>
            {t('evaluation.publicationRestriction')}
          </span>
        )}
        {renderNominate && (
          <>
            <PermissionHandler
              scopes={[
                SCOPES.canNominateSubmission,
                session?.sessionType === SESSION_TYPES.FINAL_JURY &&
                  SCOPES.chairPerson,
                session?.sessionType === SESSION_TYPES.FINAL_JURY &&
                  SCOPES.staff,
              ]}
            >
              {showGoldDecidedAction && (
                <span className={styles.nominatedWrapper}>
                  <img
                    src={getLogo('gold')}
                    className={combine(
                      styles.nominatedIcon,
                      nominated && styles.active,
                      goldDecidedActionDisabled && styles.disabled,
                      submission.chairPersonMustDecide && styles.disabled,
                      (isReadOnly || isPreview) && styles.disabled
                    )}
                    size="2x"
                    role="button"
                    onClick={handleNominate}
                    disabled={hasPublishRestriction}
                  />
                </span>
              )}
            </PermissionHandler>

            <PermissionHandler
              scopes={[SCOPES.canSuggestSubmissionForNomination, SCOPES.staff]}
            >
              <FontAwesomeIcon
                className={combine(
                  styles.suggestForNominationIcon,
                  suggestedForNominationPreselection && styles.dashed, // TODO: Implement dashed styling
                  suggestedForNomination && styles.active,
                  role !== ROLES.chairPerson &&
                    submission.chairPersonMustDecide &&
                    styles.disabled,
                  role == ROLES.chairPerson &&
                    !submission.chairPersonMustDecide &&
                    styles.disabled,
                  isAdminView && !suggestedForNomination && styles.readOnly,
                  !isAdminView &&
                    session?.prizeMoneyModeEnabled &&
                    styles.readOnly,
                  isReadOnly || (isPreview && styles.disabled)
                )}
                size="2x"
                icon={
                  suggestedForNomination || suggestedForNominationPreselection
                    ? faStarSolid
                    : faStar
                }
                role="button"
                onClick={handleSuggestNominationClick}
              />
            </PermissionHandler>
          </>
        )}
        {renderDelegateChairperson && (
          <PermissionHandler scopes={[SCOPES.canLetChairpersonDecide]}>
            <FontAwesomeIcon
              className={combine(
                styles.icon,
                submission.chairPersonMustDecide && styles.active,
                evaluationStatus === EVALUATION_STATUS.decidedByChairperson &&
                  styles.disabled,
                isReadOnly && styles.disabled
              )}
              size="2x"
              icon={faHand}
              role="button"
              onClick={handleChairPersonMustDecide}
            />
          </PermissionHandler>
        )}
        {renderComment && !impersonatingJurorId && (
          <div className={styles.commentContainer}>
            <FontAwesomeIcon
              className={combine(
                styles.commentIcon,
                submission.comment && styles.active,
                numComments > 0 && styles.active,
                role !== ROLES.chairPerson &&
                  submission.chairPersonMustDecide &&
                  styles.disabled,
                isReadOnly && styles.disabled
              )}
              role="button"
              size="2x"
              icon={faMessageCode}
              onClick={onCommentModalShow}
            />
            {(role === ROLES.groupLeader ||
              jurorGroup?.jurorGroupPhase ===
                JUROR_GROUP_PHASES.discussionPhase) &&
              numComments > 0 && (
                <span
                  className={combine(
                    styles.badge,
                    role !== ROLES.chairPerson &&
                      submission.chairPersonMustDecide &&
                      styles.disabled,
                    isReadOnly && styles.disabled
                  )}
                  onClick={onCommentModalShow}
                >
                  {numComments}
                </span>
              )}
          </div>
        )}
        {renderPrizeMoney && (
          <FontAwesomeIcon
            className={combine(
              styles.nominatedIcon,
              nominated && styles.active,
              !isAdminView && styles.readOnly,
              isReadOnly && styles.disabled
            )}
            size="2x"
            icon={faMedal}
            role="button"
            onClick={handlePrizeMoneyNomination}
          />
        )}
      </div>
    </>
  );
};

ActionsColumn.propTypes = {
  submission: PropTypes.object,
  updateSubmission: PropTypes.func,
  isAdminView: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  isDetailPage: PropTypes.bool,
};

ActionsColumn.defaultProps = {
  submission: {},
  updateSubmission: () => {},
  isAdminView: false,
  isReadOnly: false,
  isDetailPage: false,
};

export default ActionsColumn;
